import type { Node, Edge } from '@xyflow/react';
import { IOType } from '@erp/shared-types/node-graph';
import type { WorkflowDefinitionResponse } from '@erp/rpc';
import { buildStepLayoutGroupGraph } from './stepLayoutGroups';

export interface MMGraphSocket {
  id: string;
  /**
   * Display label. Held on the entry rather than read from the record key,
   * because the key is the socket id: two sockets can share a display name
   * (a switch case labelled `default` alongside the fixed `default` socket),
   * and keying by name silently collapsed them to one.
   */
  name: string;
  ioType: IOType;
  socketType: string; // e.g. 'string', 'entity_reference', 'json', etc.
  /**
   * Carried from the persisted definition. Load-bearing for dynamic-input steps:
   * `hydrateDynamicStepTypeData` rebuilds `_stepTypeData.inputSockets` from these
   * records, and `graphToDefinition` reads the flag back on save — both default a
   * missing value to `true`, so dropping it here silently marks every optional
   * input required.
   */
  required?: boolean;
}

export interface NodeDataType extends Record<string, unknown> {
  label: string;
  inputs: {
    [key: string]: MMGraphSocket;
  };
  outputs: {
    [key: string]: MMGraphSocket;
  };
  stepType?: string;
  category?: string;
  config?: Record<string, unknown>;
  emits?: string[];
}

/**
 * Convert a workflow definition to React Flow nodes and edges.
 *
 * The returned node list also contains the definition's step layout groups (visual
 * container nodes), with member steps reparented onto them. Consumers that mean
 * "the steps" should go through `toAbsoluteStepNodes`.
 */
export function definitionToGraph(
  definition: WorkflowDefinitionResponse,
  options?: { workflowId?: string; isReadOnly?: boolean },
): {
  nodes: Node<NodeDataType>[];
  edges: Edge[];
} {
  // Create a map of step IDs to indices for edge mapping
  const stepIdToIndex = new Map<string, number>();
  definition.steps.forEach((step, index) => {
    stepIdToIndex.set(step.id, index);
  });

  // Convert definition steps to React Flow nodes
  const stepNodes: Node<NodeDataType>[] = definition.steps.map((step) => {
    // Convert sockets to the format expected by StepNode
    const inputs: { [key: string]: MMGraphSocket } = {};
    const outputs: { [key: string]: MMGraphSocket } = {};

    step.sockets.inputs.forEach((socket) => {
      inputs[socket.id] = {
        id: socket.id,
        name: socket.name,
        ioType: IOType.INPUT,
        socketType: socket.type,
        required: socket.required,
      };
    });

    step.sockets.outputs.forEach((socket) => {
      outputs[socket.id] = {
        id: socket.id,
        name: socket.name,
        ioType: IOType.OUTPUT,
        socketType: socket.type,
      };
    });

    return {
      id: step.id,
      type: 'stepNode',
      position: step.position,
      data: {
        label: step.name,
        inputs,
        outputs,
        stepType: step.type,
        category: step.category,
        config: step.config,
        emits: step.emits,
      },
    };
  });

  // Convert definition edges to React Flow edges
  const edges: Edge[] = definition.edges
    .map((edge) => {
      const sourceIndex = stepIdToIndex.get(edge.sourceStepId);
      const targetIndex = stepIdToIndex.get(edge.targetStepId);

      if (sourceIndex === undefined || targetIndex === undefined) {
        // Skip edges with invalid step references
        return null;
      }

      return {
        id: edge.id, // Preserve original edge UUID
        source: edge.sourceStepId,
        sourceHandle: edge.sourceSocketId,
        target: edge.targetStepId,
        targetHandle: edge.targetSocketId,
        animated: true,
        type: 'deletable',
      } as Edge;
    })
    .filter((edge): edge is Edge => edge !== null);

  const nodes = buildStepLayoutGroupGraph(
    stepNodes,
    definition.stepLayoutGroups ?? [],
    options,
  ) as Node<NodeDataType>[];

  return { nodes, edges };
}
